import { cn } from '@/lib/utils';
import type { InstallTree as Tree, InstallTreeNode } from '../installTree';
import { DependencyRow } from './DependencyRow';
import { KindMonogram } from './KindMonogram';

/** Past this depth the list structure carries the nesting; the page stops indenting. */
const MAX_INDENTED_DEPTH = 3;

/**
 * What an install brings in, drawn as the tree it is (spec 109 US2).
 *
 * Each entry is the row the flat list always had — the same monogram, name and
 * plan — so nothing about reading one entry changes; only where it sits does.
 * A linked workflow's own entries hang under it on the rail the outline uses
 * for a phase's steps, in neutral tokens, because the phase accents mean
 * phases. The list structure itself carries depth and parent for assistive
 * technology (FR-005, US2 #7).
 *
 * `settledFor` takes an entry's place in the install order, so the rows still
 * flip innermost first after an install however deep each one is drawn
 * (FR-008).
 */
export function InstallTree({
  tree,
  settledFor,
}: {
  tree: Tree;
  settledFor: (order: number) => boolean;
}) {
  return (
    <ul
      aria-label="Needs"
      className="border-border divide-border flex flex-col divide-y rounded-lg border"
    >
      <Nodes nodes={tree.nodes} depth={0} settledFor={settledFor} />
      {tree.workspaceOwn.map((key) => (
        <li
          key={`own/${key}`}
          className="flex items-center gap-3 px-3 py-2 text-sm"
        >
          <KindMonogram kind="workflow" size="sm" />
          <span className="min-w-0 flex-1 truncate font-mono">{key}</span>
          <span className="text-muted-foreground text-xs whitespace-nowrap">
            this workspace’s own
          </span>
        </li>
      ))}
    </ul>
  );
}

function Nodes({
  nodes,
  depth,
  settledFor,
}: {
  nodes: InstallTreeNode[];
  depth: number;
  settledFor: (order: number) => boolean;
}) {
  return nodes.map((node) => (
    <DependencyRow
      key={`${node.entry.dep.kind}/${node.entry.dep.key}`}
      row={node.entry}
      settled={settledFor(node.order)}
    >
      {node.children.length > 0 && (
        <ul
          className={cn(
            'border-border flex flex-col border-l-2',
            depth < MAX_INDENTED_DEPTH && 'mt-2 ml-3',
          )}
        >
          <Nodes
            nodes={node.children}
            depth={depth + 1}
            settledFor={settledFor}
          />
        </ul>
      )}
    </DependencyRow>
  ));
}
